import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus } from "lucide-react";
import { photoUrl } from "../../lib/photos";
import { BRAND, errMsg } from "./constants";

export function BrandMark({ property, size = 44, tid }) {
  const name = property.brand_name || property.name || "H";
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [property.logo]);
  return property.logo && !broken
    ? <img data-testid={tid} src={photoUrl(property.logo)} alt={`${name} logo`} onError={() => setBroken(true)} className="rounded-xl object-cover border bg-white shrink-0" style={{ width: size, height: size, borderColor: BRAND.line }} />
    : <span data-testid={tid} className="rounded-xl grid place-items-center font-display font-bold text-white shrink-0" style={{ width: size, height: size, background: BRAND.green, fontSize: size * 0.45 }}>{name.slice(0, 1).toUpperCase()}</span>;
}

export function BrandingPanel({ property, authFetch, API, onSaved }) {
  const [f, setF] = useState({ brand_name: "", tagline: "", logo: null });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ brand_name: property.brand_name || "", tagline: property.tagline || "", logo: property.logo || null }); }, [property]);

  const upload = async (file) => {
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      const r = await authFetch({ method: "post", url: `${API}/upload/listing-photo`, data: form, headers: { "Content-Type": "multipart/form-data" } });
      setF((x) => ({ ...x, logo: r.data.path }));
    } catch (e) { toast.error(errMsg(e, "Logo upload failed")); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await authFetch({ method: "patch", url: `${API}/homestay/properties/${property.id}/branding`, data: { brand_name: f.brand_name, tagline: f.tagline, ...(f.logo ? { logo: f.logo } : {}) } });
      toast.success("Branding saved"); onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save branding")); }
    finally { setBusy(false); }
  };
  const cls = "mt-1 w-full rounded-xl border-2 px-3 py-2 text-sm";
  const preview = { ...property, ...f, brand_name: f.brand_name || property.name };

  return (
    <div data-testid="branding-panel" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="text-sm font-bold" style={{ color: BRAND.green }}>Your brand</div>
      <p className="text-xs" style={{ color: BRAND.muted }}>Your logo and name appear on your dashboard, public page, guest app and the daily summary.</p>
      <div className="mt-3 flex items-center gap-3 rounded-2xl p-3" style={{ background: "#F6F8F7" }} data-testid="branding-preview">
        <BrandMark property={preview} size={52} />
        <div className="min-w-0">
          <div className="font-display text-xl font-bold truncate" style={{ color: BRAND.green }}>{preview.brand_name}</div>
          <div className="text-xs truncate" style={{ color: BRAND.muted }}>{f.tagline || "Your tagline here"}</div>
        </div>
      </div>
      <label data-testid="branding-logo-upload" className="mt-3 inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-xs font-bold cursor-pointer" style={{ borderColor: BRAND.green, color: BRAND.green }}>
        <ImagePlus className="w-4 h-4" /> {f.logo ? "Change logo" : "Upload logo"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      </label>
      <label className="block mt-3 text-[10px] uppercase tracking-widest font-bold" style={{ color: BRAND.muted }}>Brand name</label>
      <input data-testid="branding-name-input" className={cls} value={f.brand_name} maxLength={60} onChange={(e) => setF({ ...f, brand_name: e.target.value })} placeholder={property.name} />
      <label className="block mt-2 text-[10px] uppercase tracking-widest font-bold" style={{ color: BRAND.muted }}>Tagline</label>
      <input data-testid="branding-tagline-input" className={cls} value={f.tagline} maxLength={80} onChange={(e) => setF({ ...f, tagline: e.target.value })} placeholder="Stay | Study | Grow" />
      <button disabled={busy} data-testid="branding-save-btn" onClick={save} className="mt-3 rounded-full px-5 py-2 text-sm font-bold text-white disabled:opacity-60" style={{ background: BRAND.green }}>Save branding</button>
    </div>
  );
}
